import { Answers, QuestionCodes } from "../../UserJourney/UserJourneyMap";

// Musculoskeletal Assessment (MSK)
//
// Rule-based risk tiering (no numeric composite score):
// - No Risk: "No Pain" selected in pain location — not a displayed rating; users skip the result page
// - low: severity 1-3 AND no moderate/severe/unable-to-do answer in the activity or work questions
// - moderate: severity 4-6 OR exactly one moderate answer across the activity and work questions
// - high: severity >= 7 OR at least one severe/unable-to-do answer OR two or more moderate answers

const MSK_RATING_KEYS = {
    noRisk: "No Risk",
    low: "low",
    moderate: "moderate",
    high: "high"
};

const totalRiskLevel = 3;

// Choice answers are stored as their English labels, so impact answers are compared against the
// English text exactly, case-insensitively.
const normalise = value =>
    String(value)
        .trim()
        .toLowerCase();

const isMatch = (value, ...candidates) => {
    if (value == null) return false;
    const str = normalise(value);
    return candidates.some(c => c != null && c !== "" && normalise(c) === str);
};

// Answers from the activity and work questions that count as a moderate or a severe impact. The two
// questions use different wording, so each tier lists the matching label from both.
const MODERATE_IMPACT_ANSWERS = [Answers.msk.activityModerateDifficulty, Answers.msk.moderately];
const SEVERE_IMPACT_ANSWERS = [
    Answers.msk.activitySevereDifficulty,
    Answers.msk.activityUnableToDo,
    Answers.msk.severely
];

const MskGuidelineDefinition = [
    {
        min: 7,
        max: 10,
        key: MSK_RATING_KEYS.high,
        label: "ranges.high_impact",
        rangeInfo: "7-10",
        riskLevel: 3 / totalRiskLevel
    },
    {
        min: 4,
        max: 6,
        key: MSK_RATING_KEYS.moderate,
        label: "ranges.moderate_impact",
        rangeInfo: "4-6",
        riskLevel: 2 / totalRiskLevel
    },
    {
        min: 1,
        max: 3,
        key: MSK_RATING_KEYS.low,
        label: "ranges.low_impact",
        rangeInfo: "1-3",
        riskLevel: 1 / totalRiskLevel
    }
];

// Look up a definition by its rating key. Use this instead of array indexing so that
// the order of MskGuidelineDefinition above is presentation-agnostic and reorderable.
const getDefinition = key => MskGuidelineDefinition.find(def => def.key === key);

// Definitions ordered most-severe first, for the result page's risk bars (HIGH at the top
// down to LOW). Derived from riskLevel, so it stays correct if the source order changes.
const MskGuidelineDisplayOrder = [...MskGuidelineDefinition].sort((a, b) => b.riskLevel - a.riskLevel);

/**
 * Count answers that match a specific level.
 * Accepts a single value or array of values.
 * @param {string|string[]} value
 * @param {string[]} candidates
 * @returns {number}
 */
const countMatches = (value, ...candidates) => {
    if (value == null) return 0;
    const arr = Array.isArray(value) ? value : [value];
    return arr.reduce((n, v) => n + (isMatch(v, ...candidates) ? 1 : 0), 0);
};

/**
 * Calculate MSK rating from answers.
 * @param {object} answers - { MSK_PAIN_LOCATION, MSK_PAIN_SEVERITY, MSK_ACTIVITY_IMPACT, MSK_WORK_IMPACT }
 * @returns {{ score: number, key: string, label: string, rangeInfo: string, ranges: object[] }}
 */
function msk(answers) {
    const painLocation = answers[QuestionCodes.mskPainLocation];
    const severityRaw = answers[QuestionCodes.mskPainSeverity];
    const activity = answers[QuestionCodes.mskActivityImpact];
    const work = answers[QuestionCodes.mskWorkImpact];

    const locations = Array.isArray(painLocation) ? painLocation : painLocation != null ? [painLocation] : [];
    const hasNoPain = locations.length === 0 || locations.some(l => isMatch(l, Answers.msk.noPain));

    // "No Pain" is a skip path, not a rating tier — it has no entry in MskGuidelineDefinition
    // and never reaches the result page. The "No Risk" key is still recorded for data reporting.
    if (hasNoPain) {
        return {
            score: 0,
            key: MSK_RATING_KEYS.noRisk,
            label: "ranges.no_risk",
            rangeInfo: "0",
            ranges: MskGuidelineDefinition
        };
    }

    const severity = Number.parseInt(severityRaw, 10) || 0;
    const severeCount = countMatches(activity, ...SEVERE_IMPACT_ANSWERS) + countMatches(work, ...SEVERE_IMPACT_ANSWERS);
    const moderateCount =
        countMatches(activity, ...MODERATE_IMPACT_ANSWERS) + countMatches(work, ...MODERATE_IMPACT_ANSWERS);

    let key;
    if (severity >= 7 || severeCount >= 1 || moderateCount >= 2) {
        key = MSK_RATING_KEYS.high;
    } else if (severity >= 4 || moderateCount === 1) {
        key = MSK_RATING_KEYS.moderate;
    } else {
        key = MSK_RATING_KEYS.low;
    }

    const def = getDefinition(key);
    return {
        score: severity,
        key: def.key,
        label: def.label,
        rangeInfo: def.rangeInfo,
        ranges: MskGuidelineDefinition
    };
}

const RiskName = "MSK";
const RiskCode = "MSK";
const GuidelineName = "SiSU MSK Guideline";
const GuidelineCode = "SISU_MSK";

export {
    msk,
    MskGuidelineDefinition,
    MskGuidelineDisplayOrder,
    getDefinition,
    RiskName,
    RiskCode,
    GuidelineName,
    GuidelineCode
};
